import React from "react";

import { INSURANCE_TYPE_OPTIONS } from "BrokerOfRecord/constants";
import classnames from "classnames";
import arrayMutators from "final-form-arrays";
import { Form } from "react-final-form";
import { useHistory, Link } from "react-router-dom";

import { useGetApplicationQuery, useUpdateApplicationMutation, useGetNetsuiteDeductionAccountsQuery } from "../api";
import BORPage from "../components/BORPage";
import { baseURL, fileUploadURL, doneURL } from "../urls";
import { H5 } from "Cobalt/Header";
import Button from "Common/Button";
import { SIFinalFormField, SIFormSelect } from "Common/forms";
import { Row, Col } from "Common/Grid";
import Icon from "Common/Icon";

import makeBem from "Utils/makeBem";

const bem = makeBem("BORMainContent");

const getTypeLabel = (planType: string) =>
  INSURANCE_TYPE_OPTIONS.find((o) => o.value === planType)?.label ?? planType.replace(/([A-Z])/g, " $1").replace(/^./, (c) => c.toUpperCase());

const planOrder = INSURANCE_TYPE_OPTIONS.map((o) => o.value);
// --- Component to render account dropdowns for a plan ---
const PlanAccountRow = ({ planType, plan, index, expenseOptions, liabilityOptions }) => {
  return (
    <Row className={`BORPlanRow-${planType} items-center mb-4 border-b pb-2`}>
      <Col sm={4}>{plan.planName}</Col>
      <Col sm={4}>
        <SIFinalFormField
          name={`accounts.${planType}[${index}].expenseAccount`}
          label="Expense Account"
          component={SIFormSelect}
          componentProps={{ options: expenseOptions }}
        />
      </Col>
      <Col sm={4}>
        <SIFinalFormField
          name={`accounts.${planType}[${index}].liabilityAccount`}
          label="Liability Account"
          component={SIFormSelect}
          componentProps={{ options: liabilityOptions }}
        />
      </Col>
    </Row>
  );
};

// --- Component to render all plans of a type ---
const PlansAccountSection = ({ planType, plans, expenseOptions, liabilityOptions }) => {
  return (
    <div className="mb-8">
      <H5 className="mb-2 capitalize">{getTypeLabel(planType)} Plans</H5>
      {plans.map((plan, i) => (
        <PlanAccountRow
          key={plan.id || plan.planName || `${planType}-${i}`}
          planType={planType}
          plan={plan}
          index={i}
          expenseOptions={expenseOptions}
          liabilityOptions={liabilityOptions}
        />
      ))}
    </div>
  );
};

// --- Main page ---
const DeductionsPage = () => {
  const { data: borApplication } = useGetApplicationQuery();
  const { data: netsuiteAccounts } = useGetNetsuiteDeductionAccountsQuery();
  const [updateApplication] = useUpdateApplicationMutation();
  const history = useHistory();

  const intakePlans = borApplication?.borApplicationIntake?.plans || {};
  const initialValues = borApplication
    ? {
        accounts: Object.fromEntries(
          Object.entries(borApplication.borApplicationIntake?.plans || {}).map(([planType, plans]) => [planType, plans.map((plan) => ({ ...plan.accounts }))]),
        ),
      }
    : {};

  // TODO: Replace these with real options fetched from API
  const expenseOptions = netsuiteAccounts?.select_options?.expense_select_options;
  const liabilityOptions = netsuiteAccounts?.select_options?.liability_select_options;

  const buildUpdatedPlans = (values) => {
    const submittedAccounts = values?.accounts || {};
    const merged = Object.fromEntries(
      Object.entries(intakePlans).map(([planType, plans]) => {
        const selectedForType = submittedAccounts[planType] || [];
        const mergedPlans = plans.map((plan, i) => {
          const submitted = selectedForType[i] || {};
          return {
            ...plan, // preserves plan.id and other fields
            accounts: {
              ...(plan.accounts || {}),
              ...(submitted || {}),
            },
          };
        });
        return [planType, mergedPlans];
      }),
    );
    return merged;
  };

  const submitHandler = async (values) => {
    try {
      const updatedPlans = buildUpdatedPlans(values);

      await updateApplication({
        id: borApplication.id,
        borApplicationIntake: {
          ...borApplication.borApplicationIntake,
          plans: updatedPlans,
        },
      }).unwrap();

      history.push(`${baseURL}${doneURL}`);
    } catch (err) {
      console.error("Save failed", err);
    }
  };

  return (
    <BORPage>
      <Button component={Link} size="medium" to={`${baseURL}${fileUploadURL}`} color="link" className={bem("backButton")} pl={0}>
        <span>
          <Icon icon="chevron-left" pr={3} />
          Back
        </span>
      </Button>

      <H5 className={bem("title")}>Set up Payroll Deductions</H5>
      <hr className={bem("divider")} />

      <Form onSubmit={submitHandler} initialValues={initialValues} mutators={{ ...arrayMutators }}>
        {({ handleSubmit }) => (
          <form className={bem("form")} onSubmit={handleSubmit}>
            {planOrder.map((planType) => {
              const plans = intakePlans[planType];
              if (!plans || !plans.length) return null;
              return (
                <PlansAccountSection key={planType} planType={planType} plans={plans} expenseOptions={expenseOptions} liabilityOptions={liabilityOptions} />
              );
            })}
            <Row>
              <Col className={classnames(bem("submit"), "text-center")}>
                <Button className={bem("submit", "button")} size="medium" type="submit">
                  Next
                </Button>
              </Col>
            </Row>
          </form>
        )}
      </Form>
    </BORPage>
  );
};

export default DeductionsPage;
